/**
 * NotifCardMoment — as irmãs da carta (`LetterMoment` em `moments.tsx`): mini
 * cartões que deslizam do canto inferior direito para avisar sobre eventos
 * sociais/sistêmicos do reformu.lat, mantendo a mesma família visual (papel
 * creme, lacre de cera, mola de entrada).
 */
import type { ReactNode } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { motion } from 'motion/react';
import { useAutoDone } from './moments';

export type NotifKind = 'invite' | 'mention' | 'review' | 'hire' | 'system';

interface NotifCardMomentProps {
  kind: NotifKind;
  onDone: () => void;
}

const NOTIF_DURATION_MS = 3400;

const PAPER_BG = 'linear-gradient(180deg,#F6F1E4,#EAE2CE)';
const PAPER_SHADOW = '0 20px 46px rgba(0,0,0,0.6), 0 0 0 1px rgba(70,55,30,0.25)';
const FLAP_BG = 'linear-gradient(180deg,#EDE4CE,#E2D7BC)';

/** deslocamentos determinísticos das faíscas do selo de avaliação */
const REVIEW_SPARKS = [
  { x: -34, y: -10, delay: 0.58 },
  { x: 30, y: -16, delay: 0.7 },
] as const;

/* ─────────────── convite (colaboração/projeto compartilhado) ─────────────── */
function InviteCard({ t }: { t: TFunction }) {
  return (
    <div
      className="relative w-[168px] rounded-[4px] px-4 pb-3 pt-4 text-center"
      style={{ background: PAPER_BG, boxShadow: PAPER_SHADOW }}
    >
      {/* cantinho de papel espiando pra fora da aba */}
      <motion.span
        aria-hidden
        initial={{ y: -4, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="absolute -top-[6px] right-[26px] z-0 h-[16px] w-[22px] rounded-t-[2px]"
        style={{ background: 'linear-gradient(180deg,#EFE7D2,#DED1AF)', transform: 'rotate(-7deg)' }}
      />
      {/* aba do envelope */}
      <span
        aria-hidden
        className="absolute -top-px left-0 right-0 z-[1] h-[26px]"
        style={{ clipPath: 'polygon(0 0, 50% 100%, 100% 0)', background: FLAP_BG }}
      />
      {/* lacre lua */}
      <motion.span
        aria-hidden
        initial={{ scale: 1.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.4, type: 'spring', stiffness: 340, damping: 13 }}
        className="relative z-10 mx-auto grid h-[26px] w-[26px] place-items-center rounded-full text-[10px]"
        style={{
          background: 'radial-gradient(circle at 36% 30%, #C8D4F5, #5B8DEF 70%)',
          color: '#141A33',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}
      >
        ✧
      </motion.span>
      <p className="relative z-10 mt-2 font-mono text-[7.5px] uppercase tracking-[0.2em] text-[#A8935F]">
        {t('hud.notifInvite', 'um convite chegou')}
      </p>
    </div>
  );
}

/* ─────────────── menção (comentaram/falaram de você) ─────────────── */
function MentionCard({ t }: { t: TFunction }) {
  return (
    <div
      className="relative w-[168px] rounded-[4px] px-4 pb-3 pt-4 text-center"
      style={{ background: PAPER_BG, boxShadow: PAPER_SHADOW }}
    >
      <motion.span
        aria-hidden
        initial={{ scale: 0, opacity: 0, rotate: -20 }}
        animate={{ scale: 1, opacity: 1, rotate: -8 }}
        transition={{ delay: 0.3, type: 'spring', stiffness: 280, damping: 14 }}
        className="absolute -top-3 -left-1 text-[16px]"
        style={{ color: '#241C36' }}
      >
        ✎
      </motion.span>
      {/* lacre roxo */}
      <motion.span
        aria-hidden
        initial={{ scale: 1.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.4, type: 'spring', stiffness: 340, damping: 13 }}
        className="relative z-10 mx-auto grid h-[22px] w-[22px] place-items-center rounded-full text-[10px]"
        style={{
          background: 'radial-gradient(circle at 36% 30%, #C4ABFC, #A78BFA 70%)',
          color: '#241C36',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}
      >
        “
      </motion.span>
      <div className="relative z-10 mt-2 inline-block">
        <p className="font-mono text-[7.5px] uppercase tracking-[0.2em] text-[#A8935F]">
          {t('hud.notifMention', 'falaram de você')}
        </p>
        {/* sublinhado dourado que se desenha sob o texto */}
        <motion.span
          aria-hidden
          initial={{ width: '0%' }}
          animate={{ width: '100%' }}
          transition={{ delay: 0.75, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="absolute bottom-[-3px] left-0 block h-[2px] rounded-full"
          style={{ background: 'linear-gradient(90deg,var(--ember),var(--ember-hot))' }}
        />
      </div>
    </div>
  );
}

/* ─────────────── avaliação recebida ─────────────── */
function ReviewCard({ t }: { t: TFunction }) {
  return (
    <div
      className="relative w-[168px] rounded-[4px] px-4 pb-3 pt-6 text-center"
      style={{ background: PAPER_BG, boxShadow: PAPER_SHADOW }}
    >
      <motion.span
        aria-hidden
        initial={{ scale: 2, opacity: 0, rotate: 0 }}
        animate={{ scale: 1, opacity: 1, rotate: -12 }}
        transition={{ delay: 0.35, type: 'spring', stiffness: 300, damping: 13 }}
        className="relative z-10 mx-auto grid h-[34px] w-[34px] place-items-center rounded-full text-[18px]"
        style={{
          background: 'radial-gradient(circle at 36% 30%, var(--ember-hot), var(--ember) 60%, #9A8756)',
          color: '#141005',
          boxShadow: '0 6px 16px rgba(0,0,0,0.45), 0 0 20px rgba(223,201,147,0.4)',
        }}
      >
        ★
      </motion.span>
      {REVIEW_SPARKS.map((sp) => (
        <motion.span
          key={`${sp.x}-${sp.y}`}
          aria-hidden
          initial={{ x: 0, y: 0, opacity: 0, scale: 0.3 }}
          animate={{ x: sp.x, y: sp.y, opacity: [0, 1, 0], scale: [0.3, 1, 0.5] }}
          transition={{ delay: sp.delay, duration: 0.9, ease: 'easeOut' }}
          className="pointer-events-none absolute left-1/2 top-3 text-[10px]"
          style={{ color: 'var(--ember-hot)' }}
        >
          ✦
        </motion.span>
      ))}
      <p className="relative z-10 mt-3 font-mono text-[7.5px] uppercase tracking-[0.2em] text-[#A8935F]">
        {t('hud.notifReview', 'uma avaliação chegou')}
      </p>
    </div>
  );
}

/* ─────────────── proposta de trabalho ─────────────── */
function HireCard({ t }: { t: TFunction }) {
  return (
    <div
      className="relative w-[168px] rounded-[4px] px-4 pb-3 pt-4 text-center"
      style={{ background: PAPER_BG, boxShadow: PAPER_SHADOW }}
    >
      {/* fita aurora atravessando o cartão na diagonal */}
      <motion.span
        aria-hidden
        initial={{ x: '-120%', opacity: 0 }}
        animate={{ x: '0%', opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        className="absolute left-[-20%] top-[38%] h-[10px] w-[140%] -rotate-[18deg]"
        style={{
          background:
            'linear-gradient(90deg, rgba(94,234,212,0), rgba(94,234,212,0.55) 40%, rgba(94,234,212,0.55) 60%, rgba(94,234,212,0))',
        }}
      />
      {/* aba do envelope */}
      <span
        aria-hidden
        className="absolute -top-px left-0 right-0 z-[1] h-[26px]"
        style={{ clipPath: 'polygon(0 0, 50% 100%, 100% 0)', background: FLAP_BG }}
      />
      {/* lacre aurora */}
      <motion.span
        aria-hidden
        initial={{ scale: 1.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ delay: 0.4, type: 'spring', stiffness: 340, damping: 13 }}
        className="relative z-10 mx-auto grid h-[26px] w-[26px] place-items-center rounded-full text-[10px]"
        style={{
          background: 'radial-gradient(circle at 36% 30%, #8FF2E0, #2E9E8A 70%)',
          color: '#08201B',
          boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        }}
      >
        ◈
      </motion.span>
      <p className="relative z-10 mt-2 font-mono text-[7.5px] uppercase tracking-[0.2em] text-[#A8935F]">
        {t('hud.notifHire', 'proposta de trabalho')}
      </p>
    </div>
  );
}

/* ─────────────── aviso do sistema ─────────────── */
function SystemCard({ t }: { t: TFunction }) {
  return (
    <div
      className="relative w-[168px] rounded-[4px] px-4 pb-3 pt-5 text-center"
      style={{
        background: 'rgba(13,20,38,0.95)',
        boxShadow: '0 20px 46px rgba(0,0,0,0.6), 0 0 0 1px rgba(168,184,232,0.2)',
      }}
    >
      {/* sino de bronze que balança */}
      <motion.span
        aria-hidden
        initial={{ rotate: 0, opacity: 0 }}
        animate={{ rotate: [0, -14, 10, -6, 0], opacity: 1 }}
        transition={{ delay: 0.35, duration: 0.9, ease: 'easeInOut' }}
        className="relative z-10 mx-auto block h-[24px] w-[22px]"
        style={{ transformOrigin: 'top center' }}
      >
        <span
          className="absolute left-1/2 top-0 h-[18px] w-[20px] -translate-x-1/2 rounded-b-full"
          style={{ background: 'linear-gradient(180deg,#D8C48F,#8A784E)', boxShadow: '0 3px 8px rgba(0,0,0,0.5)' }}
        />
        <span
          className="absolute bottom-[-2px] left-1/2 h-[4px] w-[4px] -translate-x-1/2 rounded-full"
          style={{ background: '#F2E6C4' }}
        />
      </motion.span>
      <p className="relative z-10 mt-2 font-mono text-[7.5px] uppercase tracking-[0.2em] text-[#A8B8E8]">
        {t('hud.notifSystem', 'aviso do observatório')}
      </p>
    </div>
  );
}

/* ─────────────── casca comum: mola de entrada + saída deslizando ─────────────── */
export function NotifCardMoment({ kind, onDone }: NotifCardMomentProps) {
  const { t } = useTranslation();
  useAutoDone(onDone, NOTIF_DURATION_MS);

  let card: ReactNode;
  switch (kind) {
    case 'invite':
      card = <InviteCard t={t} />;
      break;
    case 'mention':
      card = <MentionCard t={t} />;
      break;
    case 'review':
      card = <ReviewCard t={t} />;
      break;
    case 'hire':
      card = <HireCard t={t} />;
      break;
    case 'system':
      card = <SystemCard t={t} />;
      break;
  }

  return (
    <motion.div
      initial={{ x: 80, y: 12, opacity: 0, rotate: 5 }}
      animate={{ x: 0, y: 0, opacity: 1, rotate: -2 }}
      exit={{ x: 60, opacity: 0, transition: { duration: 0.35 } }}
      transition={{ type: 'spring', stiffness: 220, damping: 20 }}
      className="pointer-events-none fixed bottom-[86px] right-5 z-[135] md:bottom-8"
      aria-hidden
    >
      {card}
    </motion.div>
  );
}
